import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Bot, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../lib/api';

const ConversationsPage = () => {
  const [conversations, setConversations] = useState([]);
  const [bots, setBots] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchBots();
    fetchConversations();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchBots = async () => {
    try {
      const response = await api.get('/api/bots');
      if (response.ok) {
        const data = await response.json();
        setBots(data);
        if (data.length > 0) setSelectedBotId(data[0].bot_id);
      }
    } catch (error) {
      console.error('Failed to fetch bots:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/api/conversations');
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const createConversation = async () => {
    if (!selectedBotId) return;
    try {
      const response = await api.post(`/api/conversations?bot_id=${selectedBotId}`);
      if (response.ok) {
        const conv = await response.json();
        setConversations([conv, ...conversations]);
        loadConversation(conv.conversation_id);
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data);
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !selectedConversation || sending) return;

    const userMessage = { role: 'user', content: input, message_id: Date.now() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSending(true);

    try {
      const response = await api.post(`/api/chat/${selectedConversation.bot_id}`, {
        message: input,
        conversation_id: selectedConversation.conversation_id
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data.assistant_message]);
      }
    } catch (error) {
      console.error('Send message error:', error);
    } finally {
      setSending(false);
    }
  };

  const getBotName = (botId) => {
    const bot = bots.find(b => b.bot_id === botId);
    return bot?.name || 'Bot';
  };

  return (
    <div className="h-[calc(100vh-120px)] flex gap-6">
      {/* Conversations List */}
      <Card className="w-80 flex flex-col">
        <CardHeader className="border-b">
          <CardTitle className="text-lg">Conversas</CardTitle>
          <div className="flex gap-2 mt-2">
            <Select value={selectedBotId} onValueChange={setSelectedBotId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Selecione um bot" />
              </SelectTrigger>
              <SelectContent>
                {bots.map((bot) => (
                  <SelectItem key={bot.bot_id} value={bot.bot_id}>
                    {bot.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={createConversation} size="sm" className="bg-[#3b82f6] hover:bg-[#2563eb]">
              Nova
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <p className="text-center py-4 text-gray-500">Carregando...</p>
          ) : conversations.length === 0 ? (
            <p className="text-center py-4 text-gray-500">Nenhuma conversa</p>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => loadConversation(conv.conversation_id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedConversation?.conversation_id === conv.conversation_id
                      ? 'bg-[#3b82f6] text-white'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    <span className="text-sm font-medium truncate">{getBotName(conv.bot_id)}</span>
                  </div>
                  <p className="text-xs mt-1 opacity-70 truncate">
                    {new Date(conv.updated_at).toLocaleDateString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-[#3b82f6]" />
                {getBotName(selectedConversation.bot_id)}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={msg.message_id || idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start gap-2 max-w-[70%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-[#3b82f6]' : 'bg-gray-200'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="h-4 w-4 text-white" />
                      ) : (
                        <Bot className="h-4 w-4 text-gray-600" />
                      )}
                    </div>
                    <div
                      className={`px-4 py-2 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-[#3b82f6] text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-2 rounded-2xl">
                    <span className="animate-pulse">Digitando...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </CardContent>
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Digite sua mensagem..."
                  disabled={sending}
                />
                <Button onClick={sendMessage} disabled={sending} className="bg-[#3b82f6] hover:bg-[#2563eb]">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <CardContent className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Selecione ou crie uma conversa</p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ConversationsPage;
